import styled from 'styled-components';
import { getColor } from '../../../styling/mixins/colors';
import { fontFamilyBold, text } from '../../../styling/mixins/typography';
import { alignCenter, flexColumn } from '../../../styling/utils/flex';
import Icon from '../../Icon';
import {
  InputSize,
  InputState,
  StyledCheckboxProps,
} from '../types/Input.types';

interface StyledInputWrapperProps {
  $inputState: InputState;
  $hasIcon: boolean;
}

interface StyledInputProps {
  $inputSize: InputSize;
  $inputState: InputState;
  $hasIcon: boolean;
}

const getColorByState = (inputState: InputState) => {
  switch (inputState) {
    case 'default':
    default:
      return null;
    case 'success':
      return getColor('success');
    case 'error':
      return getColor('alert');
    case 'warning':
      return getColor('warning');
  }
};

export const StyledInputWrapper = styled.div<StyledInputWrapperProps>`
  display: flex;
  ${flexColumn}
  width: 100%;
  position: ${(p) => (p.$hasIcon ? 'relative' : null)};

  label {
    ${text('large')}
    color: ${getColor('grey', 900)};
    ${fontFamilyBold}
    margin-bottom: 0.8rem;
  }

  .helper-text {
    ${text('small')}
    margin-top: 0.8rem;
    color: ${getColor('grey', 600)};
  }

  .message {
    ${text('small')}
    display: flex;
    ${alignCenter}
    margin-top: 0.4rem;
    color: ${(p) => getColorByState(p.$inputState)};
    span {
      margin-left: 0.4rem;
    }
  }
`;

export const StyledInput = styled.input<StyledInputProps>`
  ${text('large')}
  border-radius: ${(p) => p.theme.inputs.borderRadius};
  border: 1px solid ${(p) => p.theme.inputs.borderColors.default};
  border-color: ${(p) => getColorByState(p.$inputState)};
  color: ${(p) => getColorByState(p.$inputState)};
  padding: ${(p) => p.theme.inputs.paddings[p.$inputSize]};
  width: 100%;
  resize: none;
  -webkit-appearance: textfield;
  -moz-appearance: textfield;
  padding-right: ${(p) =>
    p.$hasIcon ? p.theme.inputs.rightPaddings[p.$inputSize] : null};

  &::placeholder {
    color: ${(p) => getColorByState(p.$inputState)};
  }

  &:hover,
  &:active,
  &:focus {
    &::placeholder {
      color: ${(p) => getColorByState(p.$inputState)};
    }
  }
  &:disabled {
    border-color: ${getColor('grey', 300)};
    background-color: ${getColor('grey', 200)};
    cursor: not-allowed;

    &::placeholder {
      color: ${getColor('grey', 400)};
    }

    &:hover {
      border-color: ${getColor('grey', 300)};
    }
  }
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

export const StyledIcon = styled(Icon)<{ $inputSize: InputSize }>`
  position: absolute;
  color: ${getColor('grey', 500)};
  font-size: ${(p) => p.theme.inputs.iconSize[p.$inputSize]};
  right: ${(p) => p.theme.inputs.iconRightDistance[p.$inputSize]};
  top: 50%;
  transform: translateY(-50%);
`;

export const StyledTextArea = styled(StyledInput)`
  ${text('large')}
  width: 100%;
  height: ${(p) => p.theme.inputs.textareaSize[p.$inputSize]};
`;

export const StyledTextAreaIcon = styled(StyledIcon)`
  top: ${(p) => p.theme.inputs.iconRightDistance[p.$inputSize]};
  transform: unset;
`;

export const StyledCheckboxWrapper = styled.div<StyledCheckboxProps>`
  display: inline-flex;
  ${flexColumn}
  cursor: pointer;
  ${text('medium')}
  line-height: 1.2;
  color: ${getColor('grey', 600)};
  white-space: pre-wrap;

  label {
    display: flex;
    ${alignCenter}

    span {
      margin-left: 1.2rem;
      color: ${getColor('grey', 900)};
      cursor: pointer;
    }
  }

  .helper-text {
    ${text('small')}
    margin-top: 0.8rem;
    color: ${getColor('grey', 600)};
  }

  .message {
    ${text('small')}
    display: flex;
    ${alignCenter}
    margin-top: 0.4rem;
    color: ${(p) => getColorByState(p.$inputState)};
    span {
      margin-left: 0.4rem;
    }
  }
`;

export const CheckboxIconWrapper = styled.div`
  position: relative;
`;

export const StyledCheckbox = styled.input<StyledCheckboxProps>`
  cursor: pointer;
  position: relative;
  appearance: none;
  border-radius: 2px;
  width: ${(p) => p.theme.inputs.checkboxSizes[p.$inputSize]};
  height: ${(p) => p.theme.inputs.checkboxSizes[p.$inputSize]};
  box-sizing: border-box;
  outline: none;
  transition: all linear 0.125s;
  margin: 0;
  border: 1px solid ${getColor('grey', 400)};
  background-color: ${getColor('white')};

  &:hover,
  &:focus {
    border-color: ${getColor('primary')};
  }
  &:disabled {
    border-color: ${getColor('primary')};
    background-color: ${getColor('grey', 200)};

    &:checked {
      border-color: ${getColor('grey', 300)};
      background-color: ${getColor('grey', 200)};

      &:after {
        border: solid ${getColor('grey', 300)};
        border-width: 0 2px 2px 0;
      }
    }
  }
  &:checked {
    background-color: ${getColor('primary')};
    border-color: ${getColor('primary')};
    margin-bottom: -1px;
  }
`;

export const StyledCheckboxIcon = styled(Icon)<{ $checked: boolean }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: ${getColor('white')};
  pointer-events: none;
  width: 100% !important;
  height: 100% !important;
  opacity: ${(p) => (p.$checked ? '1' : '0')};
  transition: opacity linear 0.125s;
`;

export const StyledRadioButton = styled.input<StyledCheckboxProps>`
  cursor: pointer;
  display: inline-flex;
  ${alignCenter}
  appearance: none;
  border-radius: 50%;
  width: ${(p) => p.theme.inputs.checkboxSizes[p.$inputSize]};
  height: ${(p) => p.theme.inputs.checkboxSizes[p.$inputSize]};
  box-shadow: 0 0 0 1px ${getColor('grey', 400)};
  background-color: ${getColor('white')};
  transition: all linear 0.125s;

  &:hover,
  &:focus {
    box-shadow: 0 0 0 1px ${getColor('primary')};
  }

  &:disabled {
    background: ${getColor('grey', 200)};
    border: 4px solid ${getColor('grey', 200)};
    box-shadow: 0 0 0 1px ${getColor('grey', 300)};
  }

  &:checked {
    background: ${getColor('primary')};
    border: 4px solid ${getColor('white')};
    box-shadow: 0 0 0 1px ${getColor('primary')};

    &:disabled {
      background: ${getColor('grey', 400)};
      border: 4px solid ${getColor('grey', 200)};
      box-shadow: 0 0 0 1px ${getColor('grey', 300)};
    }
  }
`;

export const StyledInputIconWrapper = styled.div`
  position: relative;
  display: flex;
`;
